export type Notation = 'american' | 'latin'
export type Letter = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B'

export const LETTERS: readonly Letter[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B']

const NATURAL_PITCH: Record<Letter, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }

export const LATIN_NAMES: Record<Letter, string> = {
  C: 'Do',
  D: 'Re',
  E: 'Mi',
  F: 'Fa',
  G: 'Sol',
  A: 'La',
  B: 'Si',
}

/** Nombres latinos en minúscula; la comparación no distingue mayúsculas («DO», «Do», «do»). */
const LATIN_LOOKUP: readonly [string, Letter][] = [
  ['sol', 'G'],
  ['do', 'C'],
  ['re', 'D'],
  ['mi', 'E'],
  ['fa', 'F'],
  ['la', 'A'],
  ['si', 'B'],
]

export interface NoteName {
  letter: Letter
  /** -2 doble bemol … +2 doble sostenido. */
  accidental: number
}

export function pitchClass(note: NoteName): number {
  return (((NATURAL_PITCH[note.letter] + note.accidental) % 12) + 12) % 12
}

function readAccidentals(input: string, index: number): { accidental: number; length: number } {
  let accidental = 0
  let length = 0
  while (length < 2) {
    const char = input[index + length]
    if (char === '#' || char === '♯') accidental += 1
    else if (char === 'b' || char === '♭') accidental -= 1
    else break
    length += 1
  }
  return { accidental, length }
}

function readLatinName(input: string, index: number): { letter: Letter; length: number } | null {
  const rest = input.slice(index).toLowerCase()
  for (const [name, letter] of LATIN_LOOKUP) {
    if (rest.startsWith(name)) return { letter, length: name.length }
  }
  return null
}

/** Lee una nota en la posición indicada, en la notación indicada. */
export function readNote(
  input: string,
  index: number,
  notation: Notation,
): { note: NoteName; length: number } | null {
  let letter: Letter
  let length: number
  if (notation === 'latin') {
    const latin = readLatinName(input, index)
    if (!latin) return null
    letter = latin.letter
    length = latin.length
  } else {
    const char = input[index]
    if (!char || !(LETTERS as readonly string[]).includes(char)) return null
    letter = char as Letter
    length = 1
  }
  const accidentals = readAccidentals(input, index + length)
  return {
    note: { letter, accidental: accidentals.accidental },
    length: length + accidentals.length,
  }
}

/** ¿Empieza por un nombre de nota latino (Do, Re, Mi, Fa, Sol, La, Si)? */
export function startsWithLatinNote(input: string): boolean {
  return readLatinName(input, 0) !== null
}

export interface FormatOptions {
  /** ♯ y ♭ tipográficos en lugar de # y b. */
  unicode?: boolean
}

export function formatAccidental(accidental: number, options: FormatOptions = {}): string {
  const sharp = options.unicode ? '♯' : '#'
  const flat = options.unicode ? '♭' : 'b'
  return accidental > 0 ? sharp.repeat(accidental) : flat.repeat(-accidental)
}

export function formatNote(
  note: NoteName,
  notation: Notation,
  options: FormatOptions = {},
): string {
  const name = notation === 'latin' ? LATIN_NAMES[note.letter] : note.letter
  return name + formatAccidental(note.accidental, options)
}
